'use client';

import {
  BookOpen,
  Command,
  FolderKanban,
  PuzzleIcon as PuzzlePiece,
  Users,
  ExternalLink,
  Home,
} from 'lucide-react';
import Link from 'next/link';
import * as React from 'react';

import { useConfigurations } from '@/hooks/useConfigurations';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/shared/shadcn/components/sidebar';
import { useAdminPermissions } from '@/components/admin/hooks/useAdminPermissions';
import { NavConfiguration } from './NavConfiguration';
import { NavManagement } from './NavManagement';
import { NavSecondary } from './NavSecondary';
import { NavUser } from './NavUser';

const FIRST_ITEM_INDEX = 0;

interface ConfigItem {
  title: string;
  url: string;
}

interface ConfigGroup {
  title: string;
  items: ConfigItem[];
}

const additionalNavConfiguration = [
  {
    title: 'General',
    items: [
      {
        title: 'Data-API Configuration',
        url: '/admin/data-api-configuration',
      },

      {
        title: 'Register in MINERVA-NET',
        url: '/admin/register-in-minerva-net',
      },
    ],
  },
];

export const AppSidebar = ({ ...props }: React.ComponentProps<typeof Sidebar>): JSX.Element => {
  const { pageConfigs: getGeneratedConfigs } = useConfigurations({ configTypes: [] });
  const { canViewProjects, canViewUsers, canViewPlugins, canViewConfig } = useAdminPermissions();
  const [adminPanelUrl, setAdminPanelUrl] = React.useState<string>('#');

  React.useEffect(() => {
    const url =
      (window as { config?: { ADMIN_PANEL_URL?: string } }).config?.ADMIN_PANEL_URL || '#';
    setAdminPanelUrl(url);
  }, []);

  // Build navigation items based on permissions
  const navManagementItems = [];

  if (canViewProjects) {
    navManagementItems.push({
      title: 'Projects',
      url: '/admin/projects',
      icon: FolderKanban,
    });
  }

  if (canViewUsers) {
    navManagementItems.push({
      title: 'Users',
      url: '/admin/users',
      icon: Users,
    });
  }

  if (canViewPlugins) {
    navManagementItems.push({
      title: 'Plugins',
      url: '/admin/plugins',
      icon: PuzzlePiece,
    });
  }

  // Transform configuration structure for NavConfiguration component
  const transformedConfigs: {
    title: string;
    url: string;
    items?: { title: string; url: string }[];
  }[] = [];

  // Transform generated configs
  if (getGeneratedConfigs && Array.isArray(getGeneratedConfigs)) {
    getGeneratedConfigs.forEach((group: ConfigGroup) => {
      if (group.items && Array.isArray(group.items) && group.items.length > FIRST_ITEM_INDEX) {
        // Create a parent item that uses the first subitem's URL
        transformedConfigs.push({
          title: group.title,
          url: group.items[FIRST_ITEM_INDEX].url, // Use first item's URL as fallback
          items: group.items,
        });
      }
    });
  }

  // Add additional configs
  additionalNavConfiguration.forEach(group => {
    if (group.items && Array.isArray(group.items) && group.items.length > FIRST_ITEM_INDEX) {
      transformedConfigs.push({
        title: group.title,
        url: group.items[FIRST_ITEM_INDEX].url, // Use first item's URL as fallback
        items: group.items,
      });
    }
  });

  const data = {
    navManagement: navManagementItems,
    navConfiguration: transformedConfigs,
    navSecondary: [
      {
        title: 'Legacy Admin',
        url: adminPanelUrl,
        icon: ExternalLink,
        external: true,
      },
      {
        title: 'API Docs',
        url: 'https://minerva-dev.lcsb.uni.lu/minerva/docs/',
        icon: BookOpen,
      },
    ],
  };

  return (
    <Sidebar variant="inset" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link href="/admin/projects">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  <Command className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">MINERVA</span>
                  <span className="truncate text-xs">Admin Panel</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Link href="/">
                <Home className="size-4" />
                <span>Minerva Platform</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavManagement items={data.navManagement} />
        {canViewConfig && data.navConfiguration.length > FIRST_ITEM_INDEX && (
          <NavConfiguration items={data.navConfiguration} />
        )}
        <NavSecondary items={data.navSecondary} className="mt-auto" />
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
};
